/**
 * Pure math of the preview gizmo (W2-02; DESIGN.md §5.6.6, rendering.md R§0-R§1, rpe-format.md F§10.1).
 *
 * Spaces:
 *  - world (Phira chart space): x in [-1, 1] across the viewport width, y in [-1/ar, 1/ar] UP, isotropic (one world
 *    unit is W/2 px on both axes); angles in world degrees counter-clockwise;
 *  - RPE: X in [-675, 675], Y in [-450, 450] up; `x = X / 675`, `y = Y / 450 / ar`; rotation clockwise-positive
 *    (`worldRot = -rpeRot`);
 *  - screen px: y DOWN (the overlay canvas), so a clockwise turn on screen is a positive RPE rotation.
 *
 * Child lines live in their parent's frame (F§10.1: `pos = pos(parent) + Rot(rot(parent)) · ownPos`), so a world
 * drag delta becomes the child's own delta through the INVERSE parent rotation, then RPE units
 * (`moveX = Δx · 675`, `moveY = Δy · 450 · ar`, DESIGN.md §5.6.6).
 */

/** RPE X units per world unit. */
export const RPE_X_PER_WORLD = 675;
/** RPE Y units per world unit times the aspect ratio (`y = Y / 450 / ar`). */
export const RPE_Y_PER_WORLD_AR = 450;

export type Axis = 'x' | 'y';

/** Rotates (x, y) by `deg` degrees counter-clockwise (y up). */
export function rotateVec(x: number, y: number, deg: number): [number, number] {
  if (deg === 0) return [x, y];
  const r = (deg * Math.PI) / 180;
  const c = Math.cos(r);
  const s = Math.sin(r);
  return [x * c - y * s, x * s + y * c];
}

/** World (chart space) point -> RPE coordinates for an aspect ratio. */
export function worldToRpe(x: number, y: number, ar: number): [number, number] {
  return [x * RPE_X_PER_WORLD, y * RPE_Y_PER_WORLD_AR * ar];
}

/** RPE coordinates -> world (chart space). */
export function rpeToWorld(X: number, Y: number, ar: number): [number, number] {
  return [X / RPE_X_PER_WORLD, Y / RPE_Y_PER_WORLD_AR / ar];
}

/**
 * A world delta seen by a line whose parent has the composed rotation `parentRotDeg` (world degrees CCW), as the
 * line's OWN delta in RPE units: own = R(-parentRot) · world, then `moveX = Δx · 675`, `moveY = Δy · 450 · ar`.
 * `parentRotDeg` is 0 for a line without a parent.
 */
export function worldDeltaToMove(dx: number, dy: number, parentRotDeg: number, ar: number): [number, number] {
  const [ox, oy] = rotateVec(dx, dy, -parentRotDeg);
  return [ox * RPE_X_PER_WORLD, oy * RPE_Y_PER_WORLD_AR * ar];
}

/** Inverse of `worldDeltaToMove`: an own RPE delta of a child line as a world delta. */
export function moveToWorldDelta(dX: number, dY: number, parentRotDeg: number, ar: number): [number, number] {
  return rotateVec(dX / RPE_X_PER_WORLD, dY / RPE_Y_PER_WORLD_AR / ar, parentRotDeg);
}

/** The larger component of a delta given in ISOTROPIC units (world units or screen px; ties go to X). */
export function dominantAxis(dX: number, dY: number): Axis {
  return Math.abs(dX) >= Math.abs(dY) ? 'x' : 'y';
}

/**
 * The axis a Shift drag locks to: the larger component of the pointer's WORLD drag delta (dx, dy) in the line's own
 * frame (inverse parent rotation `parentRotDeg`, 0 without a parent). World units are isotropic, so equal on-screen
 * travel weighs the same on both axes; the RPE delta would not (X is 675 per world unit, Y 450·ar, i.e. 800 at 16:9,
 * which would make a mostly horizontal near-diagonal drag lock to Y).
 */
export function lockAxisOf(dx: number, dy: number, parentRotDeg: number): Axis {
  const [ox, oy] = rotateVec(dx, dy, -parentRotDeg);
  return dominantAxis(ox, oy);
}

/** Zeroes the other axis of a delta when an axis lock is active. */
export function lockDelta(dX: number, dY: number, lock: Axis | null): [number, number] {
  if (lock === 'x') return [dX, 0];
  if (lock === 'y') return [0, dY];
  return [dX, dY];
}

/**
 * Screen angle in degrees of a point around a centre, CLOCKWISE-positive (screen y down), in (-180, 180].
 * A clockwise turn on screen is a positive RPE rotation.
 */
export function screenAngleDeg(cx: number, cy: number, px: number, py: number): number {
  return (Math.atan2(py - cy, px - cx) * 180) / Math.PI;
}

/** `next` moved by whole turns so that it is within 180 degrees of `prev` (continuous dragging over many turns). */
export function unwrapAngle(prev: number, next: number): number {
  let d = next - prev;
  d -= 360 * Math.round(d / 360);
  return prev + d;
}

/** Rotate-ring step in degrees for the held modifiers: 15 with Shift, free (0) with Alt, else whole degrees. */
export function rotateStep(mods: { readonly shift: boolean; readonly alt: boolean }): number {
  if (mods.shift) return 15;
  return mods.alt ? 0 : 1;
}

/** Nearest multiple of `step` (Shift = 15-degree steps on the rotate ring); `step` 0 leaves `v` as it is. */
export function snapToStep(v: number, step: number): number {
  if (!(step > 0)) return v;
  const r = Math.round(v / step) * step;
  return Object.is(r, -0) ? 0 : r;
}

/** Rounds away float noise (1e-3 RPE units is far below a pixel). */
export function tidyValue(v: number, digits = 3): number {
  const k = 10 ** digits;
  const r = Math.round(v * k) / k;
  return Object.is(r, -0) ? 0 : r;
}

/**
 * Scale factor of a length/size handle drag: the pointer's signed distance from the centre along the line axis
 * (dirX, dirY: unit vector in screen px) now, over the same at the start. Never negative (a handle dragged
 * through the centre means zero length) and 1 when the start distance is degenerate.
 */
export function lengthFactor(
  cx: number,
  cy: number,
  dirX: number,
  dirY: number,
  start: readonly [number, number],
  now: readonly [number, number],
): number {
  const d0 = (start[0] - cx) * dirX + (start[1] - cy) * dirY;
  if (Math.abs(d0) < 1e-6) return 1;
  const d1 = (now[0] - cx) * dirX + (now[1] - cy) * dirY;
  return Math.max(0, d1 / d0);
}

/** Distance from point p to segment ab (any units). */
export function pointSegmentDistance(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const vx = bx - ax;
  const vy = by - ay;
  const len2 = vx * vx + vy * vy;
  let u = len2 === 0 ? 0 : ((px - ax) * vx + (py - ay) * vy) / len2;
  u = Math.max(0, Math.min(1, u));
  const qx = ax + u * vx - px;
  const qy = ay + u * vy - py;
  return Math.sqrt(qx * qx + qy * qy);
}

/** Point in a convex or concave quadrilateral (corner order as in NoteQuadBatch), even-odd rule. */
export function pointInQuad(px: number, py: number, q: ArrayLike<number>): boolean {
  let inside = false;
  for (let i = 0, j = 3; i < 4; j = i++) {
    const xi = q[i * 2] ?? 0;
    const yi = q[i * 2 + 1] ?? 0;
    const xj = q[j * 2] ?? 0;
    const yj = q[j * 2 + 1] ?? 0;
    if (yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi || 1e-12) + xi) inside = !inside;
  }
  return inside;
}

// ---------------------------------------------------------------------------------------------------------------
// Snapping (DESIGN.md §5.6.6: canvas grid, screen centre and edges, other lines' anchors)
// ---------------------------------------------------------------------------------------------------------------

export type SnapKind = 'grid' | 'centre' | 'edge' | 'anchor';

export interface SnapOptions {
  /** Grid step in RPE units (0 = no grid). */
  readonly grid: number;
  readonly centre: boolean;
  readonly edges: boolean;
  /** Positions (RPE coordinates) of other lines' anchors. */
  readonly anchors: ReadonlyArray<readonly [number, number]>;
  /** Snap distance in RPE units per axis (the pixel threshold converted by the caller). */
  readonly thresholdX: number;
  readonly thresholdY: number;
}

export interface SnapResult {
  readonly x: number;
  readonly y: number;
  /** What each axis snapped to (null = free). */
  readonly snappedX: SnapKind | null;
  readonly snappedY: SnapKind | null;
}

interface Candidate {
  readonly v: number;
  readonly kind: SnapKind;
}

function nearest(v: number, threshold: number, candidates: readonly Candidate[]): Candidate | null {
  let best: Candidate | null = null;
  let bestD = threshold;
  for (const c of candidates) {
    const d = Math.abs(c.v - v);
    // Ties prefer the earlier (more meaningful) kind: anchors and centre come before the grid.
    if (d < bestD || (best === null && d <= threshold)) {
      best = c;
      bestD = d;
    }
  }
  return best;
}

function axisCandidates(v: number, opts: SnapOptions, axis: Axis): Candidate[] {
  const out: Candidate[] = [];
  for (const a of opts.anchors) out.push({ v: axis === 'x' ? a[0] : a[1], kind: 'anchor' });
  if (opts.centre) out.push({ v: 0, kind: 'centre' });
  if (opts.edges) {
    const e = axis === 'x' ? RPE_X_PER_WORLD : RPE_Y_PER_WORLD_AR;
    out.push({ v: -e, kind: 'edge' }, { v: e, kind: 'edge' });
  }
  if (opts.grid > 0) out.push({ v: Math.round(v / opts.grid) * opts.grid, kind: 'grid' });
  return out;
}

/**
 * Snaps a target position (RPE coordinates of the line's on-screen anchor) per axis to the nearest candidate within
 * the threshold: other lines' anchors, the screen centre, the screen edges, then the grid.
 */
export function snapPoint(x: number, y: number, opts: SnapOptions): SnapResult {
  const cx = nearest(x, opts.thresholdX, axisCandidates(x, opts, 'x'));
  const cy = nearest(y, opts.thresholdY, axisCandidates(y, opts, 'y'));
  return {
    x: cx === null ? x : cx.v,
    y: cy === null ? y : cy.v,
    snappedX: cx?.kind ?? null,
    snappedY: cy?.kind ?? null,
  };
}

/** RPE units per screen pixel on each axis, for a viewport W px wide with aspect ar (threshold conversions). */
export function rpePerPx(viewportW: number, ar: number): readonly [number, number] {
  const worldPerPx = viewportW > 0 ? 2 / viewportW : 0;
  return [worldPerPx * RPE_X_PER_WORLD, worldPerPx * RPE_Y_PER_WORLD_AR * ar];
}
